// DISP-6 — pure data-mapper for the BL (bon de livraison). No React, no formatting (the component formats with
// Settings > Formatting), no writes. Two inputs, ONE output shape:
//   slipModel(payload, { showPrices })            payload = GET /api/sales-dispatches/:slip_id
//   deliveredCopyModel(order, items, partner, { showPrices })  order = GET /api/sales-orders/:op (D10: no persisted slip, no number)
// Quantities on the slip are BASE units (sales.qty); a stamped line (sale_unit_ratio > 0) is shown in its sale unit
// with the base figure kept as a hint ("20 trays (400 units)"); prices are per base unit, shown per sale unit (x ratio).
const num = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const clean = (x) => Math.round(x * 1e6) / 1e6;            // strips float noise (0.1+0.2); display rounding stays with the formatters
const txt = (v) => { const s = v == null ? '' : String(v).trim(); return s || null; };

// Delivery address precedence (D8): the slip's own deliver_to snapshot, else the customer's delivery_address, else none.
export function pickAddress(slip, customer) {
  const own = txt(slip?.deliver_to); if (own) return { text: own, source: 'slip' };
  const pa = txt(customer?.delivery_address); if (pa) return { text: pa, source: 'partner' };
  return { text: null, source: 'none' };
}

// One row. `qty` / `received` are what the document prints (sale unit when stamped, else base).
export function lineRow(l, { showPrices = false, received = null } = {}) {
  const ratio = num(l.sale_unit_ratio) > 0 && txt(l.sale_unit_label) ? num(l.sale_unit_ratio) : null;
  const base = num(l.quantity);
  const per = ratio || 1;
  const price = l.price == null ? null : clean(num(l.price) * per);
  return {
    item: txt(l.item_name) || '—',
    unit: ratio ? txt(l.sale_unit_label) : (txt(l.base_unit_label) || txt(l.item_unit)),
    qty: clean(base / per),
    baseQty: base, baseUnit: ratio ? (txt(l.base_unit_label) || txt(l.item_unit)) : null,   // hint only when stamped
    received: received == null ? null : clean(num(received) / per),
    from: txt(l.node_name),
    price: showPrices ? price : null,
    amount: showPrices && l.price != null ? clean(base * num(l.price)) : null,
  };
}

// Totals per unit label (mixed units are never added together); amount total only in price mode.
export function totalsOf(rows, showPrices) {
  const by = new Map();
  for (const r of rows) { const k = r.unit || ''; const t = by.get(k) || { unit: r.unit, qty: 0, received: 0 }; t.qty = clean(t.qty + r.qty); t.received = clean(t.received + (r.received || 0)); by.set(k, t); }
  return { byUnit: [...by.values()], amount: showPrices ? clean(rows.reduce((a, r) => a + (r.amount || 0), 0)) : null };
}

// Banner/watermark state. Drafts have no number; a cancelled slip keeps the one it had.
export const stateOf = (status) => (['draft', 'open', 'delivered', 'cancelled'].includes(status) ? status : 'open');

export function slipModel(p, { showPrices = false } = {}) {
  if (!p) return null;
  const voided = p.void === true;   // GES-431 DIRECT-BL: derived by the API when the sale of a direct BL is cancelled (the number stays)
  const state = voided ? 'void' : stateOf(p.status);
  const pickup = p.delivery_method === 'pickup';
  const delivered = state === 'delivered';
  const rows = (p.lines || []).map((l) => lineRow(l, { showPrices, received: delivered ? l.delivered_qty : null }));
  const froms = [...new Set(rows.map((r) => r.from).filter(Boolean))];
  return {
    kind: 'slip', state, watermark: state === 'draft' || state === 'cancelled' || state === 'void' ? state : null, voidAt: voided ? (p.void_at || null) : null,
    number: state === 'draft' ? null : txt(p.dispatch_no),
    date: p.dispatch_date || null, eta: p.kind === 'direct' ? null : (p.eta || p.dispatch_date || null),   // a direct-sale BL (db/763) has no transit: not printed
     orderRef: p.operation_id || p.order?.operation_id || null,
    customer: txt(p.customer_name) || txt(p.customer?.name),
    pickup, address: pickup ? { text: null, source: 'none' } : pickAddress(p, p.customer), vehicle: txt(p.vehicle), driver: txt(p.driver), from: froms.join(', ') || null,
    phone: pickup ? txt(p.pickup_phone) : null, idNote: pickup ? txt(p.pickup_id_note) : null,   // a pickup prints the collector block and the pickup location, never a delivery address
    pickupPlace: pickup ? (txt(p.pickup_location_text) || [...new Set((p.pickup_nodes || []).map((n) => txt(n.node_name)).filter(Boolean))].join(', ') || froms.join(', ') || null) : null,
    notes: txt(p.notes), cancelReason: state === 'cancelled' ? txt(p.cancel_reason) : null,
    showPrices, showReceived: delivered, writeIn: state === 'draft' || state === 'open', signatures: state !== 'cancelled' && state !== 'void',
    rows, totals: totalsOf(rows, showPrices),
  };
}

// D10: a delivered BL printed from the order, no slip row, no number. Lines = what the order payload says was
// delivered (qty_delivered > 0, not cancelled); date = the last delivery date, else the order date.
export function deliveredCopyModel(order, items = [], partner = null, { showPrices = false } = {}) {
  if (!order) return null;
  const h = order.header || {}; const unitOf = new Map((items || []).map((i) => [i.id, i]));
  const src = (order.lines || []).filter((l) => l.delivery_status !== 'cancelled' && num(l.qty_delivered) > 0);
  const rows = src.map((l) => {
    const it = unitOf.get(l.item_id) || {};
    const r = lineRow({ quantity: l.qty_delivered, item_name: l.item_name || it.name, item_unit: it.unit, base_unit_label: l.base_unit_label, sale_unit_label: l.sale_unit_label,
      sale_unit_ratio: l.sale_unit_ratio, price: l.unit_price ?? l.net_price, node_name: l.storage_name }, { showPrices });
    return r;
  });
  const dates = src.map((l) => l.last_delivery_date).filter(Boolean).sort();
  const froms = [...new Set(rows.map((r) => r.from).filter(Boolean))];
  return {
    kind: 'copy', pickup: false, state: 'delivered', watermark: null, number: null,
    date: dates.length ? dates[dates.length - 1] : (h.last_delivery_date || h.op_date || null), eta: null, orderRef: h.operation_id || order.operation_id || null,
    customer: txt(h.customer_name), address: pickAddress(null, partner), vehicle: null, driver: null, from: froms.join(', ') || null, notes: null, cancelReason: null,
    showPrices, showReceived: false, writeIn: false, signatures: true, rows, totals: totalsOf(rows, showPrices),
  };
}
